import React, { useCallback } from 'react';

// --- PEER REVIEW DATA ---
// Reviews from the team members, tailored to Abhay's Web3/App developer portfolio.
const peerReviewDataForAbhay = [
    {
        name: 'Fredy García', 
        role: 'Digital Strategist & Implementer (MBA)',
        quote: "Abhay's portfolio clearly articulates the **business impact** of his Web3 solutions. The strategic framing of decentralized value is excellent. I'd advise ensuring every case study emphasizes **ROI** and long-term scalability.",
        rating: 5,
        avatarUrl: "https://via.placeholder.com/60/805ad5/ffffff?text=FG",
        focus: 'Strategy & Business Value'
    },
    {
        name: 'Maritza García', 
        role: 'E-Commerce Consultant',
        quote: "His e-commerce platform and app projects demonstrate strong **conversion-focused design** and reliable performance. Abhay clearly understands the need for fast, secure checkouts—critical for high-volume sales environments.",
        rating: 5,
        avatarUrl: "https://via.placeholder.com/60/667eea/ffffff?text=MG",
        focus: 'Conversion & Reliability'
    },
    {
        name: 'Leonardo Durán', 
        role: 'Web Developer, UI/UX/SEO Expert',
        quote: "The technical foundation is sound. Specifically, the **clean architecture** in his React projects and the clear focus on **core web vitals (speed/SEO)** are impressive. A true full-stack professional.",
        rating: 5,
        avatar_url: "https://via.placeholder.com/60/48bb78/ffffff?text=LD",
        focus: 'Technical Quality & SEO'
    },
    {
        name: 'Solimar Cedeño', 
        role: 'Copywriter',
        quote: "The way Abhay presents his complex Web3 work is **accessible and persuasive**. His project descriptions successfully translate technical code into clear client benefits, which is vital for winning enterprise projects.",
        rating: 4,
        avatarUrl: "https://via.placeholder.com/60/f6ad55/ffffff?text=SC",
        focus: 'Communication & Persuasion'
    },
    {
        name: 'David Perez', 
        role: 'Design Leader, UX/UI',
        quote: "Abhay consistently nails the transition from design mockups to **pixel-perfect, functional code**. His commitment to **responsive design** and component reusability makes the frontend code a pleasure to review.",
        rating: 5,
        avatarUrl: "https://via.placeholder.com/60/ed64a6/ffffff?text=DP",
        focus: 'Design Implementation & UX'
    },
];

// --- STYLES ---
const styles = {
    // Styles for the individual card container (AbhayFeedbackCard)
    card: {
        backgroundColor: '#1a202c', // Dark background
        borderRadius: '1rem',
        padding: '2rem',
        maxWidth: '350px',
        minHeight: '250px', 
        margin: '1.5rem',
        boxShadow: '0 10px 15px -3px rgba(167, 139, 250, 0.2), 0 4px 6px -2px rgba(167, 139, 250, 0.1)',
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 300ms ease', // For hover effect
    },
    quoteText: {
        fontSize: '1.125rem',
        color: '#fff',
        fontStyle: 'italic',
        marginBottom: '1.5rem',
        lineHeight: '1.6',
    },
    starContainer: {
        color: '#f6ad55', // Gold color for active stars
        marginBottom: '1rem',
        fontSize: '1.25rem',
    },
    authorSection: {
        marginTop: 'auto', 
        display: 'flex',
        alignItems: 'center',
        paddingTop: '1rem', 
        borderTop: '1px solid #2d3748', 
    },
    avatar: {
        width: '3rem',
        height: '3rem',
        borderRadius: '50%',
        marginRight: '1rem',
        objectFit: 'cover',
    },
    clientName: {
        fontSize: '1rem',
        fontWeight: '600',
        color: '#a78bfa', // Purple accent
    },
    clientTitle: {
        fontSize: '0.875rem',
        color: '#cbd5e0', 
    },
    // Styles for the parent section (AbhayPeerReviewSection)
    section: {
        backgroundColor: '#000', 
        padding: '5rem 1rem', 
        fontFamily: 'Inter, sans-serif'
    },
    container: {
        maxWidth: '72rem', 
        margin: '0 auto', 
        textAlign: 'center'
    },
    heading: {
        color: '#a78bfa', 
        fontSize: '2.5rem', 
        fontWeight: '800', 
        marginBottom: '1rem'
    },
    subheading: {
        color: '#cbd5e0', 
        fontSize: '1.125rem', 
        marginBottom: '3rem'
    }
};

// --- HELPER FUNCTION ---
/**
 * Renders star icons based on the numeric rating.
 */
const renderStars = (rating) => {
    const totalStars = 5;
    let stars = [];
    for (let i = 0; i < totalStars; i++) {
        stars.push(
            <span key={i} style={{ color: i < rating ? '#f6ad55' : '#4a5568' }}>
                {i < rating ? '★' : '☆'}
            </span>
        );
    }
    return stars;
};


// --- REUSABLE CARD COMPONENT ---
/**
 * Component to display a single peer review card.
 */
const AbhayFeedbackCard = ({ review }) => {

    // Manual hover handlers for inline CSS visual effects
    const handleMouseEnter = useCallback((e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
    }, []);

    const handleMouseLeave = useCallback((e) => {
        e.currentTarget.style.transform = 'none';
    }, []);

    if (!review) return null; 

    return (
        <div 
            style={styles.card}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Rating Stars */}
            <div style={styles.starContainer}>
                {renderStars(review.rating)}
            </div>

            {/* Review Quote */}
            <p style={styles.quoteText}>
                "{review.quote}"
            </p>

            {/* Author Info */}
            <div style={styles.authorSection}>
                
                <div>
                    <p style={styles.clientName}>{review.name}</p>
                    <p style={styles.clientTitle}>{review.role}</p>
                </div>
            </div>
        </div>
    );
};


// --- PARENT SECTION COMPONENT ---
/**
 * Component to display the entire grid of peer reviews.
 */
const AbhayPeerReviewSection = () => (
    <section id="team" style={styles.section}>
        <div style={styles.container}>
            <h2 style={styles.heading}>PEER FEEDBACK</h2>
            <p style={styles.subheading}>
                Strategic and technical reviews of Abhay's Web3 and Full-Stack work from industry leaders.
            </p>
            
            {/* Grid/Flex container to hold the cards */}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
                {peerReviewDataForAbhay.map((review, index) => (
                    // Render the reusable card for each review
                    <AbhayFeedbackCard key={index} review={review} />
                ))}
            </div>
        </div>
    </section>
);

export default AbhayPeerReviewSection;